import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { ArrowLeft, LayoutGrid, Loader2 } from 'lucide-react';
import { db, ensureAuth } from '../utils/firebase';
import { useAuth } from '../utils/AuthContext';
import { isPortalStaffUser } from '../utils/userRoles';
import { resolvePortalMarkupAuthorName } from '../utils/portalMarkupAuthor';
import { usePortalInspirationBoardEngagementSession, resolvePortalActivitySource } from '../utils/portalActivityLogger';
import { getPortalAccessMeta, verifyPortalPinServer } from '../utils/portalPinService';
import InspirationBoard from '../components/board/InspirationBoard';
import type { BoardCommentAuthor } from '../components/board/comments/BoardCommentsContext';
import './PortalBoardPage.css';

interface PortalSlice {
  id: string;
  clientName?: string;
  leadId?: string;
  projectName?: string;
}

/**
 * Full-page inspiration board for portal clients. Reached from the design fee
 * step on the portal splash — requires a verified PIN session (or staff login);
 * otherwise it bounces back to the portal splash to collect the PIN.
 */
const PortalBoardPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const isStaffPortalUser = !!user && isPortalStaffUser(user.role);

  const dealId = searchParams.get('project');
  const [portalSlice, setPortalSlice] = useState<PortalSlice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isStakeholderViewer =
    !!slug && sessionStorage.getItem(`portal_stakeholder_${slug}`) === 'true';

  const activitySource = useMemo(
    () =>
      resolvePortalActivitySource({
        user,
        isStakeholderViewer,
      }),
    [user, isStakeholderViewer],
  );

  const boardSessionReady =
    !!portalSlice?.id && !!dealId && !loading && !authLoading && !error;

  usePortalInspirationBoardEngagementSession(
    portalSlice?.id,
    dealId,
    activitySource,
    boardSessionReady,
    portalSlice?.projectName ? { projectName: portalSlice.projectName } : undefined,
  );

  useEffect(() => {
    if (!slug || authLoading) return;

    const pinVerified = sessionStorage.getItem(`portal_verified_${slug}`) === 'true';
    if (!pinVerified && !isStaffPortalUser) {
      navigate(`/portal/${slug}${dealId ? `?project=${dealId}` : ''}`, { replace: true });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        await ensureAuth();
        // Load by portalId — slug list queries are denied under PIN-scoped rules.
        const accessMeta = await getPortalAccessMeta(slug);
        if (!accessMeta.requiresPin && !isStaffPortalUser) {
          await verifyPortalPinServer(slug, '');
        }
        const snap = await getDoc(doc(db, 'clientPortals', accessMeta.portalId));
        if (cancelled) return;
        if (!snap.exists()) {
          setError('Portal not found');
          return;
        }
        const data = snap.data();
        const proj = dealId ? data.projects?.[dealId] : undefined;
        setPortalSlice({
          id: snap.id,
          clientName: data.clientName,
          leadId: data.leadId,
          projectName: proj?.projectName || undefined,
        });
      } catch (err) {
        console.error('[PortalBoard] Failed to load portal:', err);
        if (!cancelled) setError('Failed to load the inspiration board');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, dealId, authLoading, isStaffPortalUser, navigate]);

  const commentAuthor = useMemo((): BoardCommentAuthor => ({
    uid: user?.id || 'unknown',
    displayName: resolvePortalMarkupAuthorName({
      isStaffPortalUser,
      userName: user?.name,
      userEmail: user?.email,
      clientDisplayName: portalSlice?.clientName,
    }),
    canWrite: !isStakeholderViewer,
    kind: isStaffPortalUser ? 'staff' : 'client',
  }), [user, isStaffPortalUser, isStakeholderViewer, portalSlice?.clientName]);

  const backToPortal = () =>
    navigate(`/portal/${slug}${dealId ? `?project=${dealId}` : ''}`);

  if (!slug || !dealId) {
    return (
      <div className="portal-board-page">
        <div className="portal-board-status">No project specified.</div>
      </div>
    );
  }

  if (loading || authLoading) {
    return (
      <div className="portal-board-page">
        <div className="portal-board-status">
          <Loader2 size={22} className="portal-board-spinner" />
          <span>Loading inspiration board…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="portal-board-page">
        <div className="portal-board-status">{error}</div>
      </div>
    );
  }

  return (
    <div className="portal-board-page">
      <header className="portal-board-header">
        <button
          type="button"
          className="portal-board-back"
          onClick={backToPortal}
          aria-label="Back to project portal"
        >
          <ArrowLeft size={18} />
          <span>Back to portal</span>
        </button>
        <div className="portal-board-title">
          <LayoutGrid size={16} aria-hidden />
          <span>Inspiration Board</span>
          {portalSlice?.projectName && (
            <span className="portal-board-project">· {portalSlice.projectName}</span>
          )}
        </div>
        <div className="portal-board-hint">
          {isStakeholderViewer
            ? 'View-only — browse the board and comments.'
            : 'Drag in images, paste links, double-click to edit notes'}
        </div>
      </header>
      <div className="portal-board-stage">
        <InspirationBoard
          dealId={dealId}
          migrateFromLeadId={portalSlice?.leadId}
          embedded={false}
          readOnly={isStakeholderViewer}
          commentAuthor={commentAuthor}
        />
      </div>
    </div>
  );
};

export default PortalBoardPage;
